import { useEffect } from 'react';

// Canonical site origin = origin publik yang sama dengan API (satu domain).
// Production: REACT_APP_BACKEND_URL=https://tokosipil.id — JANGAN hardcode hostname preview.
export const SITE_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
export const DEFAULT_TITLE = 'TokoSipil.id — Marketplace Produk Digital Konstruksi';
export const DEFAULT_DESC = 'Marketplace produk digital konstruksi Indonesia: gambar kerja, RAB, template Excel, dokumen tender & proyek untuk profesional teknik sipil.';
export const seoUrl = (path = '/') => `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
export const OG_DEFAULT = '/og-default.jpg'; // static brand fallback 1200x630 (public/)
export const stripHtml = (s) => (s || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * SEO head manager ringan untuk SPA (tanpa dependensi/framework baru).
 * Mengatur: <title>, meta description, canonical, Open Graph, Twitter card,
 * robots (noindex), dan JSON-LD. Cleanup penuh saat unmount/pindah route:
 * tag yang dibuat dihapus, title/description kembali ke default brand —
 * metadata halaman sebelumnya tidak pernah tertinggal (Product A -> Product B aman).
 */
export function useSeo({ title, description, canonical, ogImage, ogType = 'website', noindex = false, jsonLd = null } = {}) {
  const ldKey = JSON.stringify(jsonLd || null);
  useEffect(() => {
    const fullTitle = title ? (title.includes('TokoSipil.id') ? title : `${title} — TokoSipil.id`) : DEFAULT_TITLE;
    const desc = description || DEFAULT_DESC;
    const img = ogImage || seoUrl(OG_DEFAULT); // fallback brand card bila halaman tak punya gambar
    document.title = fullTitle;

    const meta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        el.setAttribute('data-seo', '1');
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    meta('name', 'description', desc);
    if (noindex) meta('name', 'robots', 'noindex, nofollow');
    meta('property', 'og:title', fullTitle);
    meta('property', 'og:description', desc);
    meta('property', 'og:type', ogType);
    meta('property', 'og:image', img);
    meta('property', 'og:site_name', 'TokoSipil.id');
    if (canonical) meta('property', 'og:url', canonical);
    meta('name', 'twitter:card', 'summary_large_image');
    meta('name', 'twitter:title', fullTitle);
    meta('name', 'twitter:description', desc);
    meta('name', 'twitter:image', img);

    let link = document.head.querySelector('link[rel="canonical"]');
    if (canonical) {
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        link.setAttribute('data-seo', '1');
        document.head.appendChild(link);
      }
      link.href = canonical;
    } else if (link) {
      // Canonical build-time prerender (tanpa data-seo) dipertahankan HANYA bila masih menunjuk
      // route yang sedang aktif (loading state di halaman prerender) — crawler (GSC WRS) bisa
      // snapshot sebelum data tiba. Canonical basi milik route lain tetap dibuang.
      const fromPrerender = link.getAttribute('data-seo') !== '1';
      const strip = (u) => u.replace(/\/+$/, '');  // /produk/x/ vs /produk/x = halaman sama
      const matchesRoute = fromPrerender && strip(new URL(link.href).pathname) === strip(window.location.pathname);
      if (!matchesRoute) link.remove();
    }

    // Dedupe JSON-LD: halaman hasil build-time prerender sudah membawa data-seo ld+json
    // di raw HTML — hapus dulu agar tidak dobel setelah React hydrate.
    document.head.querySelectorAll('script[type="application/ld+json"][data-seo="1"]').forEach((el) => el.remove());
    const ldScripts = [];
    const schemas = Array.isArray(jsonLd) ? jsonLd.filter(Boolean) : (jsonLd ? [jsonLd] : []);
    schemas.forEach((schema) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo', '1');
      s.textContent = JSON.stringify(schema);
      document.head.appendChild(s);
      ldScripts.push(s);
    });

    return () => {
      document.title = DEFAULT_TITLE;
      const d = document.head.querySelector('meta[name="description"]');
      if (d) d.setAttribute('content', DEFAULT_DESC);
      document.head.querySelectorAll('[data-seo="1"]').forEach((el) => el.remove());
      const l = document.head.querySelector('link[rel="canonical"][data-seo="1"]');
      if (l) l.remove();  // hanya canonical buatan useSeo — canonical prerender dibiarkan
    };
  }, [title, description, canonical, ogImage, ogType, noindex, ldKey]);
}
